import { InstancedMesh, Matrix4, Mesh, Object3D, Quaternion, Sprite, StaticDrawUsage, Vector3, } from 'three/webgpu';
import { uniform } from 'three/tsl';
import { CurveAtlas } from './bake.js';
import { EmissionScheduler } from './emitter.js';
import { evalColorAtBirth, evalScalarAtBirth } from './providers.js';
import { evalCurve } from './math/curve.js';
import { curlNoise3 } from './math/noise.js';
import { mulberry32 } from './math/prng.js';
import { graphCurveNodes } from './graph.js';
import { resolveAsset } from './assets.js';
import { beamGeometry, builtinMeshGeometry, procMeshGeometry } from './geometry.js';
import { TrailBuffers } from './trail.js';
import { buildBeamMaterial, buildBillboardMaterial, buildMeshMaterial, buildTrailMaterial, createParticleAttributes, PARTICLE_STRIDE, P_ANGVEL, P_BIRTH, P_CENTER, P_LIFETIME, P_ROTATION, P_SEED, P_SIZE, P_STRETCH, P_TINT, P_VELOCITY, } from './material.js';
const DEG2RAD = Math.PI / 180;
// Scratch vectors — the update loop is allocation-free.
const sPos = new Vector3();
const sDir = new Vector3();
const sB1 = new Vector3();
const sB2 = new Vector3();
const sUp = new Vector3(0, 1, 0);
const sX = new Vector3(1, 0, 0);
const sTint = [1, 1, 1, 1];
const sCurl = [0, 0, 0];
// World-space gravity: the pull is fixed in the scene, so it has to be resolved
// back into whatever frame the particles are simulated in, once per frame.
const sDown = new Vector3();
const sWorldQuat = new Quaternion();
function randomUnit(out, rng) {
    const z = rng() * 2 - 1;
    const phi = rng() * Math.PI * 2;
    const s = Math.sqrt(Math.max(0, 1 - z * z));
    return out.set(s * Math.cos(phi), z, s * Math.sin(phi));
}
function sampleShape(shape, rng, pos, dir) {
    switch (shape.type) {
        case 'point':
            pos.set(0, 0, 0);
            randomUnit(dir, rng);
            break;
        case 'sphere': {
            randomUnit(dir, rng);
            const r = shape.shell ? shape.radius : shape.radius * Math.cbrt(rng());
            pos.copy(dir).multiplyScalar(r);
            break;
        }
        case 'cone': {
            const a = rng() * Math.PI * 2;
            const rad = shape.radius * Math.sqrt(rng());
            pos.set(Math.cos(a) * rad, 0, Math.sin(a) * rad);
            const half = shape.angle * DEG2RAD;
            const cosT = 1 - rng() * (1 - Math.cos(half));
            const sinT = Math.sqrt(Math.max(0, 1 - cosT * cosT));
            const phi = rng() * Math.PI * 2;
            dir.set(sinT * Math.cos(phi), cosT, sinT * Math.sin(phi));
            break;
        }
        case 'box':
            pos.set((rng() - 0.5) * shape.size[0], (rng() - 0.5) * shape.size[1], (rng() - 0.5) * shape.size[2]);
            randomUnit(dir, rng);
            break;
        case 'ring': {
            const arc = (shape.arc ?? 360) * DEG2RAD;
            const a = rng() * arc;
            pos.set(Math.cos(a) * shape.radius, 0, Math.sin(a) * shape.radius);
            if (shape.direction === 'up')
                dir.set(0, 1, 0);
            else if (shape.direction === 'inward')
                dir.set(-Math.cos(a), 0, -Math.sin(a));
            else
                dir.set(Math.cos(a), 0, Math.sin(a));
            break;
        }
        case 'edge':
            pos.set((rng() - 0.5) * shape.length, 0, 0);
            dir.set(0, 1, 0);
            break;
    }
}
/** Deviate `dir` by up to `spreadRad` (cone around the original direction). */
function applySpread(dir, spreadRad, rng) {
    const theta = spreadRad * rng();
    const phi = rng() * Math.PI * 2;
    if (theta <= 0)
        return;
    const ref = Math.abs(dir.y) < 0.99 ? sUp : sX;
    sB1.crossVectors(dir, ref).normalize();
    sB2.crossVectors(dir, sB1);
    const sinT = Math.sin(theta);
    dir
        .multiplyScalar(Math.cos(theta))
        .addScaledVector(sB1, sinT * Math.cos(phi))
        .addScaledVector(sB2, sinT * Math.sin(phi))
        .normalize();
}
// Fixed atlas row slots (see AtlasRowAssignment).
const ROW_SIZE = 0;
const ROW_COLOR = 1;
const ROW_ROTATION = 2;
const ROW_AUX = 3;
const ROW_DISSOLVE = 4;
const ROW_GRAPH_FIRST = 5;
const GRAPH_ROW_CAPACITY = 3;
/** Structural-vs-shader split for the live-edit path: geometry/pool-affecting renderer params. */
export function rendererStructure(r) {
    switch (r.type) {
        case 'trail':
            return { type: r.type, points: r.points };
        case 'beam':
            return { type: r.type, segments: r.segments ?? 24 };
        case 'procMesh': {
            const { kind } = r.shape;
            if (kind === 'ring')
                return { type: r.type, kind, segments: r.shape.segments ?? 64 };
            return { type: r.type, shape: r.shape };
        }
        case 'mesh':
            return r;
        default:
            return { type: r.type };
    }
}
/** Shader-program-affecting renderer params (rebuild the material in place, keep instances alive). */
function rendererShaderKey(r) {
    switch (r.type) {
        case 'trail':
            return { type: r.type, widthScale: r.widthScale };
        case 'beam':
            return { type: r.type, end: r.end, curve0: r.curve0, curve1: r.curve1, widthScale: r.widthScale, tiles: r.tiles };
        case 'procMesh':
            return r.shape.kind === 'ring' ? { type: r.type, kind: 'ring', widthScale: r.shape.widthScale } : { type: r.type, kind: r.shape.kind };
        case 'flipbook':
            return r;
        case 'mesh':
            return { type: r.type, mesh: r.mesh.type === 'builtin' ? r.mesh.id : 'gltf' };
        default:
            return { type: r.type };
    }
}
/** Material def with curve/gradient CONTENTS stripped — those live in the atlas texture, not the shader program. */
function materialShaderKey(layer) {
    const m = layer.material;
    return {
        blend: m.blend,
        sprite: m.sprite,
        features: m.features
            ? {
                dissolve: m.features.dissolve
                    ? { edgeWidth: m.features.dissolve.edgeWidth, edgeColor: m.features.dissolve.edgeColor, noiseScale: m.features.dissolve.noiseScale }
                    : undefined,
                uvScroll: m.features.uvScroll,
            }
            : undefined,
        graph: m.graph
            ? {
                nodes: m.graph.nodes.map((n) => ({
                    id: n.id,
                    type: n.type,
                    params: n.type === 'curveSample' || n.type === 'gradientSample'
                        ? undefined // contents are atlas data
                        : n.params,
                })),
                edges: m.graph.edges,
            }
            : undefined,
    };
}
/**
 * One layer of one effect instance: particle pool (SoA typed arrays +
 * instanced attributes), CPU simulation, and the render object for whichever
 * renderer the layer uses.
 */
export class LayerRuntime {
    def;
    object;
    attrs;
    atlas;
    meta;
    rows = {};
    materialKey = '';
    timeUniform = uniform(0);
    capacity;
    cursor = 0;
    scheduler;
    rng;
    seed;
    worldSpace;
    trail = null;
    aliveCount = 0;
    // Cached per-def CPU-module params, refreshed in applyDef().
    gravity = 0;
    /** Gravity that pulls along world −Y whatever the emitter is rotated to. */
    worldGravity = 0;
    drag = 0;
    velCurve = null;
    turbulence = null;
    vortex = null;
    attractor = null;
    inheritVelocity = 0;
    constructor(def, meta, layerIndex, seed) {
        this.def = def;
        this.meta = meta;
        this.seed = seed;
        this.rng = mulberry32(seed);
        this.worldSpace = meta.simulationSpace === 'world';
        this.capacity = Math.max(1, def.maxParticles);
        this.attrs = createParticleAttributes(this.capacity);
        this.atlas = new CurveAtlas();
        this.scheduler = this.makeScheduler();
        this.refreshCpuModules();
        this.bakeAtlas();
        this.object = this.buildObject();
        this.materialKey = this.computeMaterialKey();
        this.object.renderOrder = layerIndex;
        this.object.visible = def.enabled;
    }
    get isTriggered() {
        return !!this.def.emitter.trigger;
    }
    matOptions() {
        return { timeUniform: this.timeUniform, attrs: this.attrs, atlas: this.atlas, rows: this.rows };
    }
    buildObject() {
        const r = this.def.renderer;
        switch (r.type) {
            case 'billboard':
            case 'stretchedBillboard':
            case 'flipbook': {
                const sprite = new Sprite(buildBillboardMaterial(this.def, this.matOptions()));
                sprite.count = this.capacity;
                sprite.frustumCulled = false;
                return sprite;
            }
            case 'beam': {
                const mesh = new InstancedMesh(beamGeometry(r.segments ?? 24), buildBeamMaterial(this.def, this.matOptions()), this.capacity);
                const identity = new Matrix4();
                for (let i = 0; i < this.capacity; i++)
                    mesh.setMatrixAt(i, identity);
                mesh.instanceMatrix.needsUpdate = true;
                mesh.instanceMatrix.setUsage(StaticDrawUsage);
                mesh.frustumCulled = false;
                return mesh;
            }
            case 'mesh':
            case 'procMesh': {
                const geometry = r.type === 'procMesh' ? procMeshGeometry(r.shape) : this.meshGeometry(r.mesh);
                const mesh = new InstancedMesh(geometry, buildMeshMaterial(this.def, this.matOptions()), this.capacity);
                // transforms live in TSL attributes; instanceMatrix stays identity
                const identity = new Matrix4();
                for (let i = 0; i < this.capacity; i++)
                    mesh.setMatrixAt(i, identity);
                mesh.instanceMatrix.needsUpdate = true;
                mesh.instanceMatrix.setUsage(StaticDrawUsage);
                mesh.frustumCulled = false;
                return mesh;
            }
            case 'trail': {
                this.trail = new TrailBuffers(this.capacity, r.points, r.minDistance ?? 0);
                const mesh = new Mesh(this.trail.geometry, buildTrailMaterial(this.def, this.matOptions()));
                mesh.frustumCulled = false;
                return mesh;
            }
        }
    }
    meshGeometry(ref) {
        if (ref.type === 'builtin')
            return builtinMeshGeometry(ref.id);
        if (ref.type === 'gltfDataUri')
            return this.loadGltfGeometry(ref.uri);
        const resolved = resolveAsset(ref.id);
        return typeof resolved === 'string' ? this.loadGltfGeometry(resolved) : builtinMeshGeometry('cube').clone();
    }
    /** GLB meshes load async; instances render as a cube until the geometry arrives. */
    loadGltfGeometry(uri) {
        const placeholder = builtinMeshGeometry('cube').clone();
        if (typeof window === 'undefined')
            return placeholder;
        void import('three/addons/loaders/GLTFLoader.js').then(({ GLTFLoader }) => {
            new GLTFLoader().load(uri, (gltf) => {
                let found = null;
                gltf.scene.traverse((o) => {
                    const m = o;
                    if (!found && m.isMesh)
                        found = m.geometry;
                });
                if (found) {
                    placeholder.copy(found);
                    placeholder.computeBoundingSphere();
                }
            });
        });
        return placeholder;
    }
    makeScheduler() {
        return new EmissionScheduler(this.def.emitter.mode, this.def.delay, this.def.duration, this.meta.duration, this.meta.loop);
    }
    refreshCpuModules() {
        this.gravity = 0;
        this.worldGravity = 0;
        this.drag = 0;
        this.velCurve = null;
        this.turbulence = null;
        this.vortex = null;
        this.attractor = null;
        this.inheritVelocity = 0;
        for (const m of this.def.modules) {
            if (!m.enabled)
                continue;
            if (m.type === 'gravity') {
                if (m.space === 'world')
                    this.worldGravity += m.strength;
                else
                    this.gravity += m.strength;
            }
            else if (m.type === 'drag')
                this.drag += m.coefficient;
            else if (m.type === 'velocityOverLife')
                this.velCurve = m.curve;
            else if (m.type === 'turbulence')
                this.turbulence = { frequency: m.frequency, amplitude: m.amplitude, scrollSpeed: m.scrollSpeed ?? 0 };
            else if (m.type === 'vortex')
                this.vortex = { strength: m.strength, axis: m.axis ?? [0, 1, 0] };
            else if (m.type === 'attractor')
                this.attractor = { position: m.position, strength: m.strength };
            else if (m.type === 'inheritVelocity')
                this.inheritVelocity = m.scale;
        }
    }
    /** Assign atlas rows and bake every curve the layer uses. Cheap; runs on any def edit. */
    bakeAtlas() {
        const rows = {};
        for (const m of this.def.modules) {
            if (!m.enabled)
                continue;
            if (m.type === 'sizeOverLife') {
                rows.size = ROW_SIZE;
                this.atlas.setCurve(ROW_SIZE, m.curve);
            }
            else if (m.type === 'colorOverLife') {
                rows.color = ROW_COLOR;
                this.atlas.setGradient(ROW_COLOR, m.gradient);
            }
            else if (m.type === 'rotationOverLife') {
                rows.rotation = ROW_ROTATION;
                rows.rotationScale = m.scale ?? 360;
                this.atlas.setCurve(ROW_ROTATION, m.curve);
            }
        }
        const r = this.def.renderer;
        if (r.type === 'trail' || r.type === 'beam') {
            rows.aux = ROW_AUX;
            this.atlas.setCurve(ROW_AUX, r.width);
        }
        else if (r.type === 'procMesh' && r.shape.kind === 'ring') {
            rows.aux = ROW_AUX;
            this.atlas.setCurve(ROW_AUX, r.shape.width);
        }
        const dissolve = this.def.material.features?.dissolve;
        if (dissolve) {
            rows.dissolve = ROW_DISSOLVE;
            this.atlas.setCurve(ROW_DISSOLVE, dissolve.curve);
        }
        if (this.def.material.graph) {
            rows.graphRows = {};
            const curveNodes = graphCurveNodes(this.def.material.graph);
            if (curveNodes.length > GRAPH_ROW_CAPACITY) {
                console.warn(`vfx-runtime: layer "${this.def.name}" material graph uses ${curveNodes.length} curve/gradient nodes; only ${GRAPH_ROW_CAPACITY} atlas rows are available — extras sample as 1/white.`);
            }
            curveNodes.slice(0, GRAPH_ROW_CAPACITY).forEach((n, i) => {
                const row = ROW_GRAPH_FIRST + i;
                rows.graphRows[n.id] = row;
                if (n.type === 'curveSample')
                    this.atlas.setCurve(row, n.params?.curve ?? { points: [] });
                else
                    this.atlas.setGradient(row, n.params?.gradient ?? { mode: 'smooth', stops: [] });
            });
        }
        this.atlas.commit();
        this.rows = rows;
    }
    /** Shader-affecting bits: when these change the NodeMaterial must be rebuilt (curve *contents* don't count). */
    computeMaterialKey() {
        return JSON.stringify([materialShaderKey(this.def), rendererShaderKey(this.def.renderer), this.rows]);
    }
    /**
     * Live-edit path (studio): swap in an updated def with the same structural
     * shape (same id/renderer geometry/maxParticles). Rebakes curves in place;
     * rebuilds the material only when the shader program actually changed.
     */
    applyDef(def, meta) {
        this.def = def;
        this.meta = meta;
        this.worldSpace = meta.simulationSpace === 'world';
        this.scheduler = this.makeScheduler();
        this.refreshCpuModules();
        this.bakeAtlas();
        this.object.visible = def.enabled;
        const key = this.computeMaterialKey();
        if (key !== this.materialKey) {
            const holder = this.object;
            const old = holder.material;
            const r = def.renderer;
            if (r.type === 'billboard' || r.type === 'stretchedBillboard' || r.type === 'flipbook') {
                holder.material = buildBillboardMaterial(def, this.matOptions());
            }
            else if (r.type === 'trail') {
                holder.material = buildTrailMaterial(def, this.matOptions());
            }
            else if (r.type === 'beam') {
                holder.material = buildBeamMaterial(def, this.matOptions());
            }
            else {
                holder.material = buildMeshMaterial(def, this.matOptions());
            }
            old.dispose();
            this.materialKey = key;
        }
    }
    /** Kill all particles and rewind emission. Same seed ⇒ identical replay. */
    reset(seed = this.seed) {
        this.seed = seed;
        this.rng = mulberry32(seed);
        this.scheduler.reset();
        this.cursor = 0;
        this.aliveCount = 0;
        for (let i = 0; i < this.capacity; i++)
            this.attrs.array[i * PARTICLE_STRIDE + P_BIRTH] = -1;
        this.attrs.buffer.needsUpdate = true;
        this.trail?.reset();
        this.timeUniform.value = 0;
    }
    /**
     * Advance instance-local time from t0 to t1; spawn if `emitting` (triggered
     * layers never self-schedule — the instance feeds them events instead).
     * Birth/death positions are appended to `events` when provided.
     */
    update(t0, t1, emitting, frame, events) {
        this.timeUniform.value = t1;
        if (!this.def.enabled) {
            this.object.visible = false;
            return;
        }
        this.object.visible = true;
        const dt = t1 - t0;
        const cap = this.capacity;
        const a = this.attrs.array;
        const { gravity, worldGravity, drag, velCurve, turbulence, vortex, attractor } = this;

        // World gravity always pulls along scene −Y, so resolve that direction
        // into the frame the particles actually live in. For a world-space layer
        // that is already scene space; for a local one the emitter may be at any
        // orientation — and on a turning parent it changes every frame, which is
        // why this is recomputed here rather than cached at spawn.
        let wgx = 0;
        let wgy = 0;
        let wgz = 0;
        if (worldGravity !== 0) {
            sDown.set(0, -1, 0);
            if (!this.worldSpace) {
                this.object.updateWorldMatrix(true, false);
                this.object.matrixWorld.decompose(sPos, sWorldQuat, sB1);
                sDown.applyQuaternion(sWorldQuat.invert()).normalize();
            }
            wgx = sDown.x * worldGravity;
            wgy = sDown.y * worldGravity;
            wgz = sDown.z * worldGravity;
        }

        let alive = 0;
        let anyDied = false;
        for (let i = 0; i < cap; i++) {
            const p = i * PARTICLE_STRIDE;
            const b = a[p + P_BIRTH];
            if (b < 0)
                continue;
            const lf = a[p + P_LIFETIME];
            const pc = p + P_CENTER;
            const pv = p + P_VELOCITY;
            if (t1 - b >= lf) {
                a[p + P_BIRTH] = -1;
                anyDied = true;
                if (events)
                    events.deaths.push(a[pc], a[pc + 1], a[pc + 2]);
                continue;
            }
            alive++;
            if (dt <= 0)
                continue;
            let vx = a[pv];
            let vy = a[pv + 1];
            let vz = a[pv + 2];
            if (gravity !== 0)
                vy -= gravity * dt;
            if (worldGravity !== 0) {
                vx += wgx * dt;
                vy += wgy * dt;
                vz += wgz * dt;
            }
            if (turbulence) {
                const f = turbulence.frequency;
                const scroll = t1 * turbulence.scrollSpeed;
                curlNoise3(a[pc] * f, a[pc + 1] * f + scroll, a[pc + 2] * f, sCurl);
                vx += sCurl[0] * turbulence.amplitude * dt;
                vy += sCurl[1] * turbulence.amplitude * dt;
                vz += sCurl[2] * turbulence.amplitude * dt;
            }
            if (vortex) {
                // tangential acceleration: strength · (axis × r)
                const ox = this.worldSpace ? frame.position.x : 0;
                const oy = this.worldSpace ? frame.position.y : 0;
                const oz = this.worldSpace ? frame.position.z : 0;
                const rx = a[pc] - ox;
                const ry = a[pc + 1] - oy;
                const rz = a[pc + 2] - oz;
                const [ax, ay, az] = vortex.axis;
                vx += (ay * rz - az * ry) * vortex.strength * dt;
                vy += (az * rx - ax * rz) * vortex.strength * dt;
                vz += (ax * ry - ay * rx) * vortex.strength * dt;
            }
            if (attractor) {
                const dx = attractor.position[0] - a[pc];
                const dy = attractor.position[1] - a[pc + 1];
                const dz = attractor.position[2] - a[pc + 2];
                const d2 = dx * dx + dy * dy + dz * dz;
                const k = (attractor.strength / (1 + d2)) * dt;
                vx += dx * k;
                vy += dy * k;
                vz += dz * k;
            }
            if (drag !== 0) {
                const k = Math.max(0, 1 - drag * dt);
                vx *= k;
                vy *= k;
                vz *= k;
            }
            a[pv] = vx;
            a[pv + 1] = vy;
            a[pv + 2] = vz;
            const vs = velCurve ? evalCurve(velCurve, (t1 - b) / lf) : 1;
            a[pc] += vx * vs * dt;
            a[pc + 1] += vy * vs * dt;
            a[pc + 2] += vz * vs * dt;
        }
        let spawned = 0;
        if (emitting && !this.isTriggered) {
            this.scheduler.advance(t0, t1, (spawnTime, normTime) => {
                this.spawnOne(spawnTime, t1, normTime, frame, 0, 0, 0, events);
                spawned++;
            });
        }
        this.aliveCount = Math.min(alive + spawned, cap);
        if (alive > 0 || spawned > 0 || anyDied) {
            this.attrs.buffer.needsUpdate = true; // one interleaved upload covers every field
        }
        this.trail?.update(this.attrs, t1);
    }
    /**
     * Sub-emitter entry point: spawn this layer's burst at an event position
     * (already in sim space). Called by EffectInstance for each parent event.
     */
    spawnTriggered(x, y, z, time, frame, events) {
        if (!this.def.enabled)
            return;
        const mode = this.def.emitter.mode;
        const count = mode.type === 'burst' ? mode.count : 1;
        for (let n = 0; n < count; n++) {
            this.spawnOne(time, time, 0, frame, x, y, z, events);
        }
        this.aliveCount = Math.min(this.aliveCount + count, this.capacity);
        this.attrs.buffer.needsUpdate = true;
    }
    spawnOne(spawnTime, now, normTime, frame, originX, originY, originZ, events) {
        const i = this.cursor;
        this.cursor = (this.cursor + 1) % this.capacity;
        const { rng } = this;
        const ctx = { rng, normTime };
        const initial = this.def.emitter.initial;
        sampleShape(this.def.emitter.shape, rng, sPos, sDir);
        const spreadRad = evalScalarAtBirth(initial.spread, ctx) * DEG2RAD;
        applySpread(sDir, spreadRad, rng);
        const speed = evalScalarAtBirth(initial.speed, ctx) * frame.scale;
        const lifetime = Math.max(1e-4, evalScalarAtBirth(initial.lifetime, ctx));
        const size = evalScalarAtBirth(initial.size, ctx) * frame.scale;
        const rotation = evalScalarAtBirth(initial.rotation, ctx) * DEG2RAD;
        const angularVelocity = (initial.angularVelocity ? evalScalarAtBirth(initial.angularVelocity, ctx) : 0) * DEG2RAD;
        const stretch = this.def.renderer.type === 'stretchedBillboard'
            ? evalScalarAtBirth(this.def.renderer.stretch, ctx)
            : 0;
        evalColorAtBirth(initial.color, ctx, sTint);
        sPos.multiplyScalar(frame.scale);
        if (this.worldSpace) {
            // Bake the instance transform in at birth — particles stay behind in world space.
            sPos.applyQuaternion(frame.quaternion).add(frame.position);
            sDir.applyQuaternion(frame.quaternion);
        }
        // sub-emitter origin (parent event position, already sim-space)
        sPos.x += originX;
        sPos.y += originY;
        sPos.z += originZ;
        const a = this.attrs.array;
        const p = i * PARTICLE_STRIDE;
        const pc = p + P_CENTER;
        const pv = p + P_VELOCITY;
        const ivx = frame.velocity.x * this.inheritVelocity;
        const ivy = frame.velocity.y * this.inheritVelocity;
        const ivz = frame.velocity.z * this.inheritVelocity;
        a[pv] = sDir.x * speed + ivx;
        a[pv + 1] = sDir.y * speed + ivy;
        a[pv + 2] = sDir.z * speed + ivz;
        const remain = Math.max(0, now - spawnTime); // sub-frame catch-up for mid-frame births
        a[pc] = sPos.x + a[pv] * remain;
        a[pc + 1] = sPos.y + a[pv + 1] * remain;
        a[pc + 2] = sPos.z + a[pv + 2] * remain;
        a[p + P_BIRTH] = spawnTime;
        a[p + P_LIFETIME] = lifetime;
        a[p + P_SIZE] = size;
        a[p + P_ROTATION] = rotation;
        a[p + P_ANGVEL] = angularVelocity;
        a[p + P_STRETCH] = stretch;
        a[p + P_SEED] = rng();
        a[p + P_SEED + 1] = rng();
        a[p + P_SEED + 2] = rng();
        a[p + P_SEED + 3] = rng();
        a[p + P_TINT] = sTint[0];
        a[p + P_TINT + 1] = sTint[1];
        a[p + P_TINT + 2] = sTint[2];
        a[p + P_TINT + 3] = sTint[3];
        this.trail?.onSpawn(i, a[pc], a[pc + 1], a[pc + 2]);
        if (events)
            events.births.push(a[pc], a[pc + 1], a[pc + 2]);
    }
    dispose() {
        this.object.material.dispose();
        this.atlas.dispose();
        if (this.trail)
            this.trail.geometry.dispose();
        this.object.removeFromParent();
    }
}
